import React, { FC, useRef } from 'react';
import { List, SxProps } from '@mui/material';
import { Waypoint } from 'react-waypoint';
import { addToFileQueue, removeFromFileQueue } from '@/redux/slices/fileQueueSlice';
import { getMoreFiles, setSearchParams, getFolder, CLEAN_PATH_REGEX, clearForwardLocations } from '@/redux/slices/fileManagerSlice';
import {
  selectCurrentFolder,
  selectCurrentPath,
  selectFileSearchParams,
  selectFolderIsLoading,
  selectHasLoadedSourcesData,
  selectNextPages
} from '@/redux/selectors/fileManagerSelectors';
import { selectFileQueue } from '@/redux/selectors/fileQueueSelectors';
import LoadingBar from '@/components/_common/LoadingBar';
import FolderEmpty from './FolderEmpty';
import styled from '@emotion/styled';
import FolderBar from './FolderBar';
import NoResults from '@/components/_common/NoResults';
import { FileManagerInventoryFile } from '@/redux/slices/slice_types';
import { selectCanWriteFileManager } from '@/redux/selectors/authSelectors';
import FileItem from '../FileItem/FileItem';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import FolderColumns from './FolderColumns';
import ScrollToTopButton from '@/components/_common/ScrollToTopButton';

type FileColumn = { name?: string; paddingLeft?: number; width: number | string; textAlign?: 'right' | 'left' };

type FileColumns = FileColumn[];

export const fileColumns: FileColumns = [
  { name: 'Name', paddingLeft: 34, width: 'calc(100% - 250px)' },
  { name: 'Last Modified', width: 100, textAlign: 'right' },
  { name: 'Size', width: 100, textAlign: 'right' },
  { name: '', width: 26 }
];

interface Props {
  addAction?: any;
  selectedFiles?: any[];
  canCreateFolder?: boolean;
}

const FolderContent: FC<Props> = ({ addAction = null, selectedFiles = [], canCreateFolder }) => {
  const dispatch = useAppDispatch();
  const items = useAppSelector(selectCurrentFolder) || [];
  const fileQueueItems = useAppSelector(selectFileQueue);
  const loading = useAppSelector(selectFolderIsLoading);
  const currentPath = useAppSelector(selectCurrentPath);
  const nextPages = useAppSelector(selectNextPages);
  const hasLoadedSourcesData = useAppSelector(selectHasLoadedSourcesData);
  const canWriteFileManager = useAppSelector(selectCanWriteFileManager);
  const hasNextPage = !!nextPages[currentPath.length - 1];
  const selectedKeys = selectedFiles.map((file) => `${file.origin_slug}__${file.key}`);
  const searchResults = useAppSelector(selectFileSearchParams);
  const listKey = currentPath ? currentPath[0] : null;

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleClick = (item: FileManagerInventoryFile) => {
    if (item.ptype === 'FOLDER') {
      dispatch(clearForwardLocations());
      const path = currentPath.concat(item.name.replace(CLEAN_PATH_REGEX, ''));
      dispatch(getFolder({ path }));
    } else {
      if (fileQueueItems.find((file) => file.key === item.key && file.origin_slug === item.origin_slug)) {
        dispatch(removeFromFileQueue(item));
      } else if (addAction) {
        addAction(item);
      } else {
        dispatch(addToFileQueue(item));
      }
    }
  };

  const loadNextPage = () => {
    if (hasLoadedSourcesData) {
      dispatch(getMoreFiles({ depth: currentPath.length - 1 }));
    }
  };

  const filteredItems = items.filter((item: FileManagerInventoryFile) => {
    const cleanName = item.name.replace(/(^[/]+)|([/]+$)/g, '');
    return cleanName !== '';
  });

  return (
    <FolderContentWrapper ref={scrollContainerRef} key={listKey} component="nav" aria-label="files list">
      <LoadingBar loading={loading} sx={{ opacity: 0.3 }} />
      <FolderBar canCreateFolder={canCreateFolder} />
      <FolderColumns />
      <FolderContents loading={+loading}>
        {!filteredItems.length ? (
          searchResults?.prefix_search?.length ? (
            <NoResults
              onClear={() => {
                dispatch(setSearchParams({ prefix_search: '' }));
                dispatch(getFolder({ path: currentPath }));
              }}
              message={
                <div>
                  No files found beginning with <strong>&quot;{searchResults?.prefix_search}&quot;</strong>
                </div>
              }
            />
          ) : (
            <FolderEmpty />
          )
        ) : (
          filteredItems.map((item: FileManagerInventoryFile) => (
            <FileItem
              key={`${item.origin_slug}__${item.key}`}
              item={item}
              onClick={(e: any) => {
                e.stopPropagation();
                if (canWriteFileManager) handleClick(item);
              }}
              selected={selectedKeys?.includes(`${item.origin_slug}__${item.key}`)}
            />
          ))
        )}
        {hasNextPage && (
          <div style={{ height: 2 }}>
            <Waypoint onEnter={loadNextPage} />
          </div>
        )}
      </FolderContents>
      <ScrollToTopButton containerRef={scrollContainerRef} size="small" type="text" />
    </FolderContentWrapper>
  );
};

export default FolderContent;

const FolderContentWrapper = styled(List)<{ sx?: SxProps; component: string }>`
  height: calc(100vh - 357px);
  padding-top: 0;
  position: relative;
  top: 0;
  overflow: auto;
  & .MuiLinearProgress-root {
    position: absolute;
  }
`;

const FolderContents = styled.div<{ loading: number }>`
  opacity: ${(props) => (props.loading ? 0.3 : 1)};
  pointer-events: ${(props) => (props.loading ? 'none' : 'all')};
  height: calc(100% - 93px);
`;
